package dev.lingvo.site.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.api.Session
import dev.lingvo.site.components.marketing.AuthPageScaffold
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LSpinner
import dev.lingvo.site.ds.Space
import kotlinx.browser.document
import kotlinx.coroutines.CancellationException
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr

/**
 * Where Google sends the browser back to, after the API has done the real work.
 *
 * All this page carries is a single-use code — never the session token. The token would otherwise
 * be written into browser history, leak through `Referer` to whatever the next page loads, and
 * appear in the logs of every proxy along the way. Here it is fetched in a POST body instead, and
 * the code it was traded for is dead the moment it is used.
 */
@Page("/auth/callback")
@Composable
fun AuthCallbackPage() {
    val ctx = rememberPageContext()
    val code = ctx.route.queryParams["code"].orEmpty()
    val next = ctx.route.queryParams["next"]?.takeIf { it.startsWith("/") && !it.startsWith("//") } ?: "/app"
    var failure by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(Unit) { document.title = tr("auth_callback.signing_you_in_lingvo", "Signing you in — Lingvo") }

    LaunchedEffect(code) {
        if (code.isBlank()) {
            failure = tr(
                "auth_callback.this_sign_in_link_is_missing",
                "This sign-in link is missing its code.",
            )
            return@LaunchedEffect
        }
        try {
            Session.adoptOAuthSession(code)
            ctx.router.tryRoutingTo(next, UpdateHistoryMode.REPLACE)
        } catch (cancelled: CancellationException) {
            throw cancelled
        } catch (error: ApiException) {
            failure = error.message
        } catch (_: Throwable) {
            failure = tr(
                "auth_callback.couldnt_reach_lingvo_check_your_connection",
                "Couldn't reach Lingvo. Check your connection and try signing in again.",
            )
        }
    }

    AuthPageScaffold(
        title = if (failure == null) {
            t("auth_callback.signing_you_in", "Signing you in")
        } else {
            t("auth_callback.that_didnt_work", "That didn't work")
        },
        subtitle = if (failure == null) {
            t("auth_callback.one_moment_finishing_your_google_sign", "One moment — finishing your Google sign-in.")
        } else {
            t("auth_callback.the_sign_in_could_not_be", "The sign-in could not be completed.")
        },
        alternatePrompt = t("auth_callback.prefer_a_password", "Prefer a password?"),
        alternateAction = t("auth_callback.log_in", "Log in"),
        alternatePath = "/login",
    ) {
        Column(Modifier.fillMaxWidth().gap(Space.LG)) {
            val message = failure
            if (message == null) {
                LSpinner(label = t("auth_callback.signing_you_in", "Signing you in"))
            } else {
                LAlert(
                    title = t("auth_callback.sign_in_failed", "Sign-in failed"),
                    description = message,
                    modifier = Modifier.fillMaxWidth(),
                    variant = LAlertVariant.DANGER,
                    // The page opens on a spinner and this replaces it when the effect resolves,
                    // so it is a notice that arrives after paint rather than one that was always
                    // there — the case `live` exists for.
                    live = true,
                )
                LButton(
                    label = t("auth_callback.back_to_log_in", "Back to log in"),
                    onClick = { ctx.router.tryRoutingTo("/login") },
                    modifier = Modifier.fillMaxWidth(),
                )
            }
        }
    }
}
